/**
 * L'aperçu d'une facture, À L'ÉCRAN (item H3).
 *
 * David a demandé de pouvoir voir la facture avant de s'engager. Le point important est
 * qu'on regarde le DOCUMENT LUI-MÊME : le PDF est rendu par le serveur, avec le même
 * générateur que la pièce définitive, et affiché dans la page. Une reconstitution en
 * HTML aurait montré ce qu'on croit produire, jamais ce qui sortira de l'imprimante.
 *
 * On ne pointe pas l'iframe directement sur l'URL de l'API : elle exige un jeton, et une
 * iframe n'en porte pas. On télécharge donc les octets avec le client authentifié, puis
 * on les affiche depuis un `blob:` local — qu'on révoque à la fermeture, sinon chaque
 * ouverture laisse un PDF entier en mémoire du navigateur.
 *
 * ⚠️ Tous les navigateurs n'affichent pas un PDF dans une iframe : le Chromium livré avec
 * Playwright n'embarque aucune visionneuse, et certains navigateurs sont configurés pour
 * télécharger les PDF au lieu de les rendre. Le lien « Ouvrir dans un onglet » n'est donc
 * pas un ornement, c'est le chemin de secours quand le cadre reste blanc.
 */
import { useEffect, useState } from 'react';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Eye, Loader2, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import Modale from './Modale';

export default function ApercuFacture({ facture, onClose, onValidee }) {
  const [url, setUrl] = useState('');
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [validation, setValidation] = useState(false);

  useEffect(() => {
    let objectUrl = '';
    let annule = false;
    (async () => {
      try {
        const res = await api.get(`/factures/${facture.id}/apercu`, { responseType: 'blob' });
        if (annule) return;
        objectUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        setUrl(objectUrl);
      } catch (e) {
        setErreur(e.response?.data?.detail || "L'aperçu n'a pas pu être produit.");
      } finally {
        if (!annule) setChargement(false);
      }
    })();
    return () => {
      annule = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [facture.id]);

  const valider = async () => {
    setValidation(true);
    try {
      const r = await api.post(`/factures/${facture.id}/valider`);
      toast.success(`Facture ${r.data.numero} émise`);
      onValidee?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setValidation(false);
    }
  };

  return (
    <Modale
      titre="Aperçu avant validation"
      icone={Eye}
      taille="document"
      onClose={onClose}
      pied={
        <>
          <Button variant="ghost" onClick={onClose} data-testid="apercu-fermer">
            Fermer sans valider
          </Button>
          <Button onClick={valider} disabled={validation || !!erreur} data-testid="apercu-valider">
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {validation ? 'Validation…' : 'Valider et émettre'}
          </Button>
        </>
      }
    >
      <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-[12.5px] text-amber-900">
          Ce document porte la mention <strong>BROUILLON</strong> et n&apos;a pas encore de numéro.
          La validation lui en attribue un et le fige définitivement : une facture émise ne se
          modifie plus.
        </p>
      </div>

      <div className="border border-neutral-200 rounded-md overflow-hidden bg-neutral-50 h-[62vh]">
        {chargement && (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
          </div>
        )}
        {erreur && !chargement && (
          <div className="h-full flex items-center justify-center text-sm text-red-600 px-6 text-center">
            {erreur}
          </div>
        )}
        {url && !erreur && (
          <iframe src={url} title="Aperçu de la facture" className="w-full h-full" data-testid="apercu-iframe" />
        )}
      </div>

      {url && !erreur && (
        <p className="text-[12px] text-neutral-500">
          Le cadre reste blanc ?{' '}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-neutral-800 underline underline-offset-2"
            data-testid="apercu-nouvel-onglet"
          >
            Ouvrir l&apos;aperçu dans un onglet <ExternalLink className="w-3 h-3" />
          </a>{' '}
          — certains navigateurs n&apos;affichent pas les PDF dans une page.
        </p>
      )}
    </Modale>
  );
}
